import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';

// Looks up a track by ISRC via Spotify's isrc: search and, if found, embeds
// Spotify's own official player iframe. Spotify doesn't allow extracting
// playable audio through the API — the embed is the only supported way to
// actually hear the track, and only works for what's already released there.
function SpotifyEmbed({ isrc, compact }) {
  const [state, setState] = useState('loading'); // 'loading' | 'found' | 'not_found' | 'unavailable' | 'error'
  const [track, setTrack] = useState(null);

  useEffect(() => {
    if (!isrc) { setState('not_found'); return; }
    let cancelled = false;
    setState('loading');

    axios.get(`${API}/api/spotify/track/${encodeURIComponent(isrc)}`)
      .then(res => {
        if (cancelled) return;
        if (res.data.found) {
          setTrack(res.data.track);
          setState('found');
        } else {
          setState('not_found');
        }
      })
      .catch(err => {
        if (cancelled) return;
        setState(err.response?.status === 503 ? 'unavailable' : 'error');
      });

    return () => { cancelled = true; };
  }, [isrc]);

  if (state === 'loading') {
    return <Placeholder compact={compact}><Spinner /></Placeholder>;
  }

  if (state === 'unavailable') {
    return (
      <Placeholder compact={compact}>
        <MutedText>Spotify listening isn't set up yet</MutedText>
      </Placeholder>
    );
  }

  if (state === 'not_found') {
    return (
      <Placeholder compact={compact}>
        <MutedText>Not found on Spotify</MutedText>
      </Placeholder>
    );
  }

  if (state === 'error') {
    return (
      <Placeholder compact={compact}>
        <MutedText>Couldn't reach Spotify</MutedText>
      </Placeholder>
    );
  }

  return (
    <EmbedWrap compact={compact}>
      <iframe
        title={`Spotify — ${track.name}`}
        src={track.embedUrl}
        width="100%"
        height={compact ? 80 : 152}
        frameBorder="0"
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        loading="lazy"
        style={{ borderRadius: 'var(--radius)', display: 'block' }}
      />
    </EmbedWrap>
  );
}

const Placeholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${p => p.compact ? '52px' : '80px'};
  background: var(--bg-3);
  border: 1px solid var(--border);
  border-radius: var(--radius);
`;

const EmbedWrap = styled.div`
  width: 100%;
`;

const MutedText = styled.span`
  font-size: 11px;
  color: var(--text-3);
`;

const Spinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid var(--border);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spotify-spin 0.7s linear infinite;
  @keyframes spotify-spin { to { transform: rotate(360deg); } }
`;

export default SpotifyEmbed;
